import clsx from "clsx";
import { formatCurrency, getTotalByTransactionType } from "../../helpers/utils";
import { Tooltip } from "react-tooltip";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import useFilteredTransactions from "../../hooks/useFilteredTransactions";
import React, { useCallback, useId, useMemo, useTransition } from "react";
import {
  COUNT_UP_ANIMATION_DURATION,
  ETransactionType,
  ICON_SIZES,
} from "../../helpers/constants";
import userPreferencesStore from "../../store/userPreferences";
import CountUp from "react-countup";

import {
  MdArrowDownward,
  MdArrowUpward,
  MdOutlineSavings,
} from "react-icons/md";
import useColors from "../../hooks/useColors";
import useMainFilter from "../../hooks/useMainFilter";
import useMobile from "../../hooks/useMobile";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface IIncomeExpensesPerYear {
  filter?: "last-seven-days" | "filtered-transactions";
}

const IncomeExpensesPerYear: React.FC<IIncomeExpensesPerYear> = ({
  filter = "filtered-transactions",
}) => {
  const { t } = useTranslation();
  const incomeTooltipId = useId();
  const expensesTooltipId = useId();
  const differenceTooltipId = useId();

  const { colors } = useColors();
  const navigate = useNavigate();
  const { selectedYear } = userPreferencesStore();
  const { currencySymbol, lastSevenDaysTransactions } = useCurrentAccount();
  const { transactionsFilteredByYear } = useFilteredTransactions();
  const { setMainFilter, mainFilter } = useMainFilter();
  const { isMobile } = useMobile();
  const [isPending, startTransition] = useTransition();

  const totalIncome: number = useMemo(
    () =>
      getTotalByTransactionType({
        type: ETransactionType.INCOME,
        transactions:
          filter === "filtered-transactions"
            ? transactionsFilteredByYear
            : lastSevenDaysTransactions,
        year: selectedYear,
      }),
    [
      filter,
      transactionsFilteredByYear,
      lastSevenDaysTransactions,
      selectedYear,
    ]
  );

  const totalExpenses: number = useMemo(
    () =>
      getTotalByTransactionType({
        type: ETransactionType.EXPENSE,
        transactions:
          filter === "filtered-transactions"
            ? transactionsFilteredByYear
            : lastSevenDaysTransactions,
        year: selectedYear,
      }),
    [
      filter,
      transactionsFilteredByYear,
      lastSevenDaysTransactions,
      selectedYear,
    ]
  );

  const difference = useMemo(
    () => totalIncome - totalExpenses,
    [totalIncome, totalExpenses]
  );

  const hasDecimals = useCallback((value: any) => {
    return value % 1 !== 0;
  }, []);

  return (
    <AnimatePresence mode="wait">
      <div
        className={clsx([
          "flex",
          "overflow-x-auto",
          "items-center",
          "justify-center",
          "no-scrollbar",
        ])}
      >
        <div
          className={clsx([
            "grid",
            "grid-cols-3",
            "w-full",
            "gap-4",
            "md:gap-4",
            "py-2",
            "md:py-4",
          ])}
        >
          <motion.div
            className={clsx(["flex", "flex-col", "gap-4", "cursor-pointer"])}
            onClick={() => {
              if (mainFilter.type === ETransactionType.INCOME) {
                startTransition(() => setMainFilter({ type: "" }));
              } else {
                startTransition(() =>
                  setMainFilter({ type: ETransactionType.INCOME })
                );
              }
            }}
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            data-tooltip-id={incomeTooltipId}
            data-tooltip-content={
              isMobile
                ? ""
                : mainFilter?.type === ETransactionType.INCOME
                ? t("income_expenses_summary.show_all")
                : t("income_expenses_summary.show_income_only")
            }
            data-tooltip-position-strategy="fixed"
            data-tooltip-open-on="hover"
            data-tooltip-offset={0}
          >
            <div
              className={clsx([
                "flex",
                "w-full",
                "md:text-lg",
                "text-base",
                "justify-center",
                "items-center",
              ])}
            >
              <MdArrowDownward
                size={isMobile ? ICON_SIZES.LG : ICON_SIZES.XL}
                style={{
                  color: colors.income.bg,
                }}
              />
            </div>
            <div
              className={clsx([
                "flex",
                "w-full",
                "gap-0",
                "flex-col",
                "transition-all",
                {
                  "blur-[2px] opacity-50":
                    mainFilter?.type === ETransactionType.EXPENSE,
                },
              ])}
            >
              <div
                className={clsx([
                  "flex",
                  "w-full",
                  "md:text-lg",
                  "text-sm",
                  "justify-center",
                  "items-center",
                  "font-medium",
                  "uppercase",
                ])}
              >
                <CountUp
                  end={totalIncome}
                  duration={COUNT_UP_ANIMATION_DURATION}
                  separator=","
                  decimals={hasDecimals(totalIncome) ? 2 : 0}
                  delay={0.1}
                  preserveValue
                  prefix={currencySymbol}
                />

                {/* {formatCurrency({
                  value: totalIncome,
                  currency: currencySymbol,
                })} */}
              </div>
              <div
                className={clsx([
                  "flex",
                  "w-full",
                  "text-sm",
                  "md:text-base",
                  "justify-center",
                  "items-center",
                  "text-red-500",
                  "dark:text-cyan-400",
                ])}
              >
                {t("income_expenses_summary.income")}
              </div>
            </div>
            {!isMobile && (
              <Tooltip
                id={incomeTooltipId}
                style={{ zIndex: 99 }}
                positionStrategy="fixed"
              />
            )}
          </motion.div>

          <motion.div
            className={clsx(["flex", "flex-col", "gap-4", "cursor-pointer"])}
            onClick={() => {
              if (mainFilter.type === ETransactionType.EXPENSE) {
                startTransition(() => setMainFilter({ type: "" }));
              } else {
                startTransition(() =>
                  setMainFilter({ type: ETransactionType.EXPENSE })
                );
              }
            }}
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{
              duration: 0.7,
              ease: "easeOut",
            }}
            data-tooltip-id={expensesTooltipId}
            data-tooltip-content={
              isMobile
                ? ""
                : mainFilter?.type === ETransactionType.EXPENSE
                ? t("income_expenses_summary.show_all")
                : t("income_expenses_summary.show_expenses_only")
            }
            data-tooltip-position-strategy="fixed"
            data-tooltip-open-on="hover"
            data-tooltip-offset={0}
          >
            <div
              className={clsx([
                "flex",
                "w-full",
                "md:text-lg",
                "text-base",
                "justify-center",
                "items-center",
              ])}
            >
              <MdArrowUpward
                size={isMobile ? ICON_SIZES.LG : ICON_SIZES.XL}
                style={{
                  color: colors.expenses.bg,
                }}
              />
            </div>
            <div
              className={clsx([
                "flex",
                "w-full",
                "gap-0",
                "flex-col",
                "transition-all",
                {
                  "blur-[2px] opacity-50":
                    mainFilter?.type === ETransactionType.INCOME,
                },
              ])}
            >
              <div
                className={clsx([
                  "flex",
                  "w-full",
                  "md:text-lg",
                  "text-sm",
                  "justify-center",
                  "items-center",
                  "font-medium",
                  "uppercase",
                ])}
              >
                <CountUp
                  end={totalExpenses}
                  duration={COUNT_UP_ANIMATION_DURATION}
                  separator=","
                  decimals={hasDecimals(totalExpenses) ? 2 : 0}
                  delay={0.1}
                  preserveValue
                  prefix={currencySymbol}
                />
                {/* {formatCurrency({
                  value: totalExpenses,
                  currency: currencySymbol,
                })} */}
              </div>
              <div
                className={clsx([
                  "flex",
                  "w-full",
                  "text-sm",
                  "md:text-base",
                  "justify-center",
                  "items-center",
                  "text-red-500",
                  "dark:text-cyan-400",
                ])}
              >
                {t("income_expenses_summary.expenses")}
              </div>
            </div>
            {!isMobile && (
              <Tooltip
                id={expensesTooltipId}
                style={{ zIndex: 99 }}
                positionStrategy="fixed"
              />
            )}
          </motion.div>
          {/* {!isMobile && ( */}
          <motion.div
            className={clsx([
              "flex",
              "flex-col",
              "gap-4",
              {
                "cursor-pointer": mainFilter.type !== "",
              },
            ])}
            onClick={() => {
              if (mainFilter.type !== "") {
                startTransition(() => setMainFilter({ type: "" }));
              }
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            data-tooltip-id={expensesTooltipId}
            data-tooltip-content={
              isMobile
                ? ""
                : mainFilter?.type !== ""
                ? t("income_expenses_summary.show_all")
                : ""
            }
            data-tooltip-position-strategy="fixed"
            data-tooltip-open-on="hover"
            data-tooltip-offset={0}
          >
            <div
              className={clsx([
                "flex",
                "w-full",
                "md:text-lg",
                "text-base",
                "justify-center",
                "items-center",
              ])}
            >
              {difference >= 0 ? (
                <MdOutlineSavings
                  size={isMobile ? ICON_SIZES.LG : ICON_SIZES.XL}
                  style={{
                    color: colors.difference.positive.bg,
                  }}
                />
              ) : (
                <MdOutlineSavings
                  size={isMobile ? ICON_SIZES.LG : ICON_SIZES.XL}
                  style={{
                    color: colors.difference.negative.bg,
                  }}
                />
              )}
            </div>
            <div
              className={clsx([
                "flex",
                "w-full",
                "gap-0",
                "flex-col",
                "transition-all",
                {
                  "blur-[2px] opacity-50": mainFilter?.type,
                },
              ])}
            >
              <div
                className={clsx([
                  "flex",
                  "w-full",
                  "md:text-lg",
                  "text-sm",
                  "justify-center",
                  "items-center",
                  "font-medium",
                  "uppercase",
                ])}
              >
                {/* {formatCurrency({
                  value: difference,
                  currency: currencySymbol,
                })} */}
                <CountUp
                  end={difference}
                  duration={COUNT_UP_ANIMATION_DURATION}
                  separator=","
                  decimals={hasDecimals(difference) ? 2 : 0}
                  delay={0.1}
                  preserveValue
                  prefix={currencySymbol}
                />
              </div>
              <div
                className={clsx([
                  "flex",
                  "w-full",
                  "text-sm",
                  "md:text-base",
                  "justify-center",
                  "items-center",
                  "text-red-500",
                  "dark:text-cyan-400",
                ])}
              >
                {t("income_expenses_summary.diff")}
              </div>
            </div>
            {!isMobile && (
              <Tooltip
                id={expensesTooltipId}
                style={{ zIndex: 99 }}
                positionStrategy="fixed"
              />
            )}
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default React.memo(IncomeExpensesPerYear);
